import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { billingApi } from '@/api/ops';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Segmented } from '@/components/Select';
import { GatewayForm, GatewaysTable, gatewayQueryKey } from '@/components/ops/Gateways';
import { PageHeader, Section } from '@/components/ops/Layout';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { QueryState } from '@/components/ops/Problem';
import { LIVE } from '@/ops/live';
import { useSelectedEnvironment } from '@/ops/useEnvironment';
import { TenantPicker } from '@/components/ops/TenantPicker';

type Scope = 'platform' | 'all' | 'byo';

/**
 * Payment gateways (plan §6.4, D-CP5/D-CP13): platform rows sell OUR credits and plans; BYO rows belong to
 * an external account and carry only its customers' payments. Secrets are write-only; a row whose adapter
 * is not registered shows `unavailable` (R10) — never a 500 at checkout.
 */
export function GatewaysPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const [scope, setScope] = useState<Scope>('platform');
  const [owner, setOwner] = useState('');
  const [creating, setCreating] = useState(false);
  const ownerOk = owner.trim() !== '';

  const filter = scope === 'platform' ? { scope: 'platform' as const } : scope === 'byo' ? { owner_tenant_id: owner.trim() } : {};
  const enabled = !!env && (scope !== 'byo' || ownerOk);
  const gateways = useQuery({
    queryKey: [...gatewayQueryKey(env ?? ''), scope, scope === 'byo' ? owner.trim() : ''],
    queryFn: () => billingApi.gateways(env!, filter),
    enabled,
    ...LIVE,
  });

  return (
    <RequirePermission permission={PERMISSIONS.billingRead} what="Payment gateways">
      <PageHeader title="Payment gateways" subtitle="Selected by data (enabled, priority, currency, method) — switching gateways is enabling one row and disabling another."
        actions={
          <>
            <EnvironmentPicker env={env} environments={environments} onChange={setEnv} />
            {hasPermission(PERMISSIONS.billingManage) && env && scope !== 'byo' && (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>Add platform gateway</button>
            )}
          </>
        } />
      <div className="filterbar ops-filters">
        <Segmented label="Rows" inline value={scope} onChange={setScope}
          options={[{ value: 'platform', label: 'Platform' }, { value: 'byo', label: 'BYO of one account' }, { value: 'all', label: 'All' }]} />
        {scope === 'byo' && (
          <div style={{ width: 360 }}>
            <TenantPicker env={env} value={owner} onChange={setOwner} label="Account" placeholder="Choose the account" />
          </div>
        )}
      </div>
      {enabled && env && (
        <Section title={scope === 'platform' ? 'Platform rows' : scope === 'byo' ? 'BYO rows' : 'Every row'} flush>
          <QueryState query={gateways} service="billing">
            {(data) => <GatewaysTable env={env} gateways={data.gateways} showOwner={scope !== 'platform'} />}
          </QueryState>
        </Section>
      )}
      {creating && env && <GatewayForm env={env} ownerTenantId={null} onClose={() => setCreating(false)} />}
    </RequirePermission>
  );
}
